import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { not } from "ramda";
import React from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";

import { CREATE_ILLUSTRATION } from "../gql/mutations/illustration";
import { QUERY_ILLUSTRATIONS } from "../gql/queries/illustration";

import { Spacing } from "../types/enum";

/**
 * Types
 */
interface Props {
  open: boolean;
  onClose: () => void;
  limit: number;
  offset: number;
}

interface FormValues {
  title: string;
}

/**
 * Schema
 */
const schema = yup.object().shape({
  title: yup.string().required(),
});

const IllustrationsPageCreateCont: React.FC<Props> = ({ open, onClose, limit, offset }: Props) => {
  const { enqueueSnackbar } = useSnackbar();

  const { register, formState, handleSubmit } = useForm<FormValues>({
    mode: "all",
    resolver: yupResolver(schema),
  });

  const [createIllustration, { loading }] = useMutation(CREATE_ILLUSTRATION, {
    refetchQueries: [
      {
        query: QUERY_ILLUSTRATIONS,
        variables: {
          filters: {
            limit,
            offset,
          },
        },
      },
    ],
  });

  const onSubmit: SubmitHandler<FormValues> = async (form) => {
    const variables = {
      input: {
        title: form.title,
      },
    };
    try {
      await createIllustration({ variables });
      enqueueSnackbar("Illustration successfully added", { variant: "success" });
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  };

  return (
    <Dialog maxWidth="lg" open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box mb={Spacing.sm}>
            <Typography variant="body1" color="textPrimary">
              Illustration
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Please input the title of the illustration that you would like to add.
            </Typography>
          </Box>
          <FormControl fullWidth>
            <TextField label="Title" variant="outlined" {...register("title")} />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={not(formState.isValid) || loading}>
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default IllustrationsPageCreateCont;
